import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import type { ControlA11y } from '../../guidance/GuidedField';

/**
 * A single-select combobox (ARIA 1.2: `role="combobox"` input, `role="listbox"` of `role="option"`s, grouped), used wherever a field is chosen from a
 * reference list. It loads its options through `load(query)` (debounced, cancelled when the next search starts, always bounded by the caller) and
 * has explicit loading, empty and error states, a live region for result counts, a clear button and an optional custom entry.
 *
 * Layout: the list is positioned `fixed` from the input's rectangle, flipped above the input when there is not enough room below and sized to the
 * space that is left, so a dialog's scrolling body never clips it. It stays in the DOM next to the input (not in a portal) so assistive
 * technology keeps it inside the dialog.
 *
 * Keyboard: ArrowDown / ArrowUp / Home / End move through the options, Enter chooses, Escape closes the list (and only the list: the dialog around
 * it stays open), Tab leaves without choosing. Typing never changes the saved value by itself in `select` mode; only choosing does.
 *
 * Two modes: `select` (the value is one of the options, a custom value is possible only when `allowCustom`) and `text` (the value is free text and the
 * options are suggestions, like the role title).
 */

export interface ComboOption {
  /** What `onSelect` receives. */
  value: string;
  /** The main text and what the input shows once chosen. */
  label: string;
  /** A second line (for example a code or a source). */
  detail?: string | null | undefined;
  /** Small text at the end of the row (for example the functional area). */
  meta?: string | null | undefined;
  /** The company's own value (marked with words, not colour only). */
  custom?: boolean | undefined;
  /** A row that does something instead of choosing a value (for example "+ Add new department"). `onSelect` is not called for it. */
  actionId?: string | undefined;
}

export interface ComboGroup {
  key: string;
  label: string;
  options: ComboOption[];
}

export interface ComboLoadResult {
  groups: ComboGroup[];
  /** A sentence under the list, for example that only the first options are shown. */
  notice?: string | null | undefined;
}

export interface GroupedComboboxHandle {
  focus: () => void;
}

export interface GroupedComboboxProps {
  a11y: ControlA11y;
  mode?: 'select' | 'text';
  /** `select`: the chosen option's value ('' for none). `text`: the text itself. */
  value: string;
  /** `select`: what to show for `value` (defaults to `value`). */
  valueLabel?: string | undefined;
  load: (query: string, signal: AbortSignal) => Promise<ComboLoadResult>;
  onSelect?: (option: ComboOption) => void;
  onTextChange?: (text: string) => void;
  onAction?: (actionId: string, query: string) => void;
  /** Shows a clear button while there is a value. */
  onClear?: () => void;
  clearLabel?: string;
  /** Allows "Use “typed” as your own value". `onCustom` creates it (for example through the API) and returns the option to select. */
  allowCustom?: boolean;
  confirmCustom?: boolean;
  onCustom?: (name: string) => Promise<ComboOption>;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  /** Text under the input about the current value, for example "Custom (kept as entered)". */
  keptNote?: string | null;
  /** Text under the list (for example a request-for-help sentence). */
  listFooter?: ReactNode;
  emptyText?: string;
  className?: string;
}

const CUSTOM_ROW = '__custom__';
const RETRY_ROW = '__retry__';
const DEBOUNCE_MS = 200;
const MAX_LIST_HEIGHT = 320;
const GAP = 4;

interface Flat extends ComboOption {
  groupKey: string;
  internal?: 'custom' | 'retry';
}

type Phase = 'idle' | 'loading' | 'ready' | 'error';

export const GroupedCombobox = forwardRef<GroupedComboboxHandle, GroupedComboboxProps>(function GroupedCombobox(props, ref) {
  const {
    a11y, mode = 'select', value, valueLabel, load, onSelect, onTextChange, onAction, onClear, clearLabel = 'selection', allowCustom = false, confirmCustom = false,
    onCustom, placeholder, disabled = false, maxLength, keptNote, listFooter, emptyText = 'No matches. Try a different word.', className,
  } = props;
  const uid = useId();
  const listId = `${uid}-list`;
  const statusId = `${uid}-status`;
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [typing, setTyping] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<ComboLoadResult>({ groups: [] });
  const [errorText, setErrorText] = useState('');
  const [active, setActive] = useState(-1);
  const [position, setPosition] = useState<CSSProperties>({});
  const [pendingCustom, setPendingCustom] = useState<string | null>(null);
  const [customBusy, setCustomBusy] = useState(false);
  const [customError, setCustomError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const first = useRef(true);

  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }), []);

  const display = mode === 'text' ? value : typing ? query : (valueLabel ?? value);

  // ---- loading ----
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const delay = first.current ? 0 : DEBOUNCE_MS;
    first.current = false;
    setPhase('loading');
    const timer = window.setTimeout(() => {
      load(query.trim(), controller.signal)
        .then((r) => {
          if (controller.signal.aborted) return;
          setResult(r);
          setPhase('ready');
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setErrorText(err instanceof Error && err.message ? err.message : 'Could not load the options.');
          setPhase('error');
        });
    }, delay);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // `load` is read from the latest render on purpose: callers pass a fresh closure each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, nonce]);

  // ---- the flat list of selectable rows (group headings are not rows) ----
  const flat: Flat[] = useMemo(() => {
    const rows: Flat[] = [];
    if (phase === 'error') rows.push({ value: RETRY_ROW, label: 'Try again', groupKey: '_status', internal: 'retry' });
    for (const g of result.groups) for (const o of g.options) rows.push({ ...o, groupKey: g.key });
    const q = query.trim();
    if (allowCustom && mode === 'select' && q && !rows.some((r) => !r.internal && r.label.toLowerCase() === q.toLowerCase())) {
      rows.push({ value: CUSTOM_ROW, label: `Use “${q}” as your own value`, groupKey: '_custom', internal: 'custom', custom: true });
    }
    return rows;
  }, [result, phase, query, allowCustom, mode]);

  // The active row follows the results: the first row while typing, the chosen one when the list opens with a value.
  useEffect(() => {
    if (!open || phase !== 'ready') return;
    const selected = flat.findIndex((r) => !r.internal && r.value === value && mode === 'select');
    setActive(query.trim() ? 0 : selected);
  }, [open, phase, flat, value, query, mode]);

  // ---- position (fixed, flipped, sized to fit) ----
  const place = useCallback(() => {
    const input = wrapRef.current?.querySelector('input');
    if (!input) return;
    const rect = input.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const below = vh - rect.bottom - GAP - 8;
    const above = rect.top - GAP - 8;
    const useAbove = below < Math.min(180, MAX_LIST_HEIGHT) && above > below;
    const room = Math.max(96, Math.min(MAX_LIST_HEIGHT, useAbove ? above : below));
    setPosition({
      position: 'fixed', left: rect.left, width: rect.width, maxHeight: room,
      ...(useAbove ? { bottom: vh - rect.top + GAP } : { top: rect.bottom + GAP }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onMove = () => place();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, place, phase, result]);

  // Keeps the active row in view inside the list.
  useEffect(() => {
    if (!open || active < 0) return;
    document.getElementById(`${uid}-opt-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active, uid]);

  useEffect(() => {
    if (pendingCustom !== null) confirmRef.current?.focus();
  }, [pendingCustom]);

  function openList() {
    if (disabled) return;
    if (!open) {
      first.current = true;
      setOpen(true);
    }
  }

  function closeList(revert = true) {
    setOpen(false);
    setActive(-1);
    if (revert) {
      setTyping(false);
      setQuery('');
    }
  }

  async function runCustom(name: string) {
    if (!onCustom) return;
    setCustomBusy(true);
    setCustomError(null);
    try {
      const created = await onCustom(name);
      setPendingCustom(null);
      setTyping(false);
      setQuery('');
      onSelect?.(created);
      inputRef.current?.focus();
    } catch (err) {
      setCustomError(err instanceof Error && err.message ? err.message : 'Could not add that value.');
    } finally {
      setCustomBusy(false);
    }
  }

  function choose(row: Flat) {
    if (row.internal === 'retry') {
      setNonce((n) => n + 1);
      return;
    }
    if (row.internal === 'custom') {
      const name = query.trim();
      closeList(false);
      if (confirmCustom) setPendingCustom(name);
      else void runCustom(name);
      return;
    }
    if (row.actionId) {
      closeList();
      onAction?.(row.actionId, query.trim());
      return;
    }
    closeList();
    onSelect?.(row);
    if (mode === 'text') onTextChange?.(row.label);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openList();
        else if (flat.length > 0) setActive((i) => (i + 1) % flat.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) openList();
        else if (flat.length > 0) setActive((i) => (i <= 0 ? flat.length - 1 : i - 1));
        break;
      case 'Home':
        if (open && flat.length > 0) { e.preventDefault(); setActive(0); }
        break;
      case 'End':
        if (open && flat.length > 0) { e.preventDefault(); setActive(flat.length - 1); }
        break;
      case 'Enter': {
        if (!open) break;
        const row = flat[active];
        // An open list owns Enter: it must not submit anything around it.
        e.preventDefault();
        if (row) choose(row);
        break;
      }
      case 'Escape':
        if (open) {
          // Closes the list only: the dialog around the field stays open.
          e.preventDefault();
          e.stopPropagation();
          closeList();
        }
        break;
      case 'Tab':
        if (open) closeList();
        break;
      default:
        break;
    }
  }

  const count = flat.filter((r) => !r.internal).length;
  const statusText = !open
    ? ''
    : phase === 'loading' ? 'Searching…'
      : phase === 'error' ? errorText
        : count === 0 ? emptyText : `${count} ${count === 1 ? 'option' : 'options'} available. Use the up and down arrow keys, then Enter.`;

  // Suggestions for free text (the role title) are only worth a list when there is something to suggest.
  const showPopup = !(mode === 'text' && (phase === 'ready' || phase === 'loading') && count === 0 && !listFooter);
  let rowIndex = -1;
  const showClear = mode === 'select' && !!onClear && !!value && !disabled;

  return (
    <div ref={wrapRef} className={['jr-combobox', className].filter(Boolean).join(' ')}>
      <div className="jr-combobox__field">
        <input
          ref={inputRef}
          id={a11y.id}
          data-field={a11y['data-field']}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={open && active >= 0 ? `${uid}-opt-${active}` : undefined}
          aria-describedby={a11y['aria-describedby']}
          aria-invalid={a11y['aria-invalid']}
          aria-required={a11y['aria-required']}
          disabled={disabled}
          maxLength={maxLength}
          placeholder={placeholder}
          value={display}
          className={showClear ? 'jr-combobox__input jr-combobox__input--clearable' : 'jr-combobox__input'}
          onClick={() => { openList(); if (mode === 'select') inputRef.current?.select(); }}
          onChange={(e) => {
            const text = e.target.value;
            setQuery(text);
            setTyping(true);
            first.current = false;
            if (mode === 'text') onTextChange?.(text);
            if (!open) setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => { if (open) closeList(mode === 'select'); }}
        />
        {showClear && (
          <button
            type="button" className="jr-combobox__clear"
            onClick={() => { onClear?.(); setTyping(false); setQuery(''); inputRef.current?.focus(); }}
          >
            <span aria-hidden="true">×</span>
            <span className="jr-sr-only">Clear {clearLabel}</span>
          </button>
        )}
      </div>

      <div id={statusId} className="jr-sr-only" role="status" aria-live="polite">{statusText}</div>

      {open && showPopup && (
        <div className="jr-combobox__popup" style={position} onMouseDown={(e) => e.preventDefault()} data-testid="combobox-popup">
          <div id={listId} role="listbox" aria-label="Matching options" className="jr-combobox__list">
            {phase === 'error' && flat[0]?.internal === 'retry' && (
              <div
                id={`${uid}-opt-${++rowIndex}`} role="option" aria-selected={false}
                className={`jr-combobox__option jr-combobox__option--action${active === rowIndex ? ' jr-combobox__option--active' : ''}`}
                onClick={() => choose(flat[0]!)}
              >
                <span className="jr-combobox__label">Try again</span>
              </div>
            )}
            {result.groups.map((g) => {
              if (g.options.length === 0) return null;
              const gid = `${uid}-g-${g.key}`;
              return (
                <div key={g.key} role="group" aria-labelledby={gid} className="jr-combobox__group">
                  <div id={gid} className="jr-combobox__group-label">{g.label}</div>
                  {g.options.map((o) => {
                    const index = ++rowIndex;
                    const row = flat[index]!;
                    const chosen = mode === 'select' && !o.actionId && o.value === value;
                    return (
                      <div
                        key={`${g.key}-${o.value}-${o.actionId ?? ''}`} id={`${uid}-opt-${index}`} role="option" aria-selected={chosen}
                        className={`jr-combobox__option${o.actionId ? ' jr-combobox__option--action' : ''}${active === index ? ' jr-combobox__option--active' : ''}`}
                        onClick={() => choose(row)}
                      >
                        <span className="jr-combobox__label">{o.label}{o.custom && <span className="jr-combobox__tag"> (your company)</span>}</span>
                        {o.detail && <> <span className="jr-combobox__detail">{o.detail}</span></>}
                        {o.meta && <> <span className="jr-combobox__meta">{o.meta}</span></>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {allowCustom && flat[flat.length - 1]?.internal === 'custom' && (
              <div
                id={`${uid}-opt-${++rowIndex}`} role="option" aria-selected={false}
                className={`jr-combobox__option jr-combobox__option--action${active === rowIndex ? ' jr-combobox__option--active' : ''}`}
                onClick={() => choose(flat[flat.length - 1]!)}
              >
                <span className="jr-combobox__label">{flat[flat.length - 1]!.label}</span>
              </div>
            )}
          </div>
          {phase === 'loading' && <p className="jr-combobox__note">Searching{'…'}</p>}
          {phase === 'error' && <p className="jr-combobox__note jr-combobox__note--error">{errorText}</p>}
          {phase === 'ready' && count === 0 && <p className="jr-combobox__note">{emptyText}</p>}
          {phase === 'ready' && result.notice && <p className="jr-combobox__note">{result.notice}</p>}
          {listFooter && <div className="jr-combobox__footer">{listFooter}</div>}
        </div>
      )}

      {keptNote && !open && <p className="jr-hint jr-kept" data-testid="kept-note">{keptNote}</p>}

      {pendingCustom !== null && (
        <div role="group" aria-label="Add your own value" className="jr-inline-confirm">
          <p>
            Add <strong>“{pendingCustom}”</strong> as your own value? It is saved with this role and offered again to people in your company. It does not
            change the EPN standard options.
          </p>
          <div className="jr-actions">
            <button ref={confirmRef} type="button" className="jr-btn jr-btn--primary jr-btn--small" disabled={customBusy} onClick={() => void runCustom(pendingCustom)}>
              {customBusy ? 'Adding…' : 'Add this value'}
            </button>
            <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={customBusy} onClick={() => { setPendingCustom(null); setCustomError(null); inputRef.current?.focus(); }}>
              Cancel
            </button>
          </div>
          {customError && <p className="jr-error" role="alert">{customError}</p>}
        </div>
      )}
      {pendingCustom === null && customError && <p className="jr-error" role="alert">{customError}</p>}
    </div>
  );
});
